import React from 'react';
import './DataTable.css';

interface DataTableProps {
  headers: string[];
  rows: Array<any[]>;
  widths?: string[];
  onRowClick?: (index: number) => void;
  isSelectable?: boolean;
  isRowSelected?: (index: number) => boolean;
  isRowDisabled?: (index: number) => boolean;
  loading?: boolean;
  itemsPerPage?: number;
  page: number;
  onPageChange: (page: number) => void;
  totalItems: number;
}

const DataTable: React.FC<DataTableProps> = ({
  headers,
  rows,
  widths,
  onRowClick,
  isSelectable = false,
  isRowSelected,
  isRowDisabled,
  loading = false,
  itemsPerPage = 20,
  page,
  onPageChange,
  totalItems,
}) => {
  if (!widths) {
    widths = new Array(headers.length).fill('auto');
  }

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;

  const renderSkeletonRows = () =>
    Array(itemsPerPage)
      .fill(0)
      .map((_, index) => (
        <tr key={`skeleton-${index}`} className="phn-data-table-row">
          <td className="phn-data-table-index"></td>
          {headers.map((_, colIndex) => (
            <td key={colIndex} style={{ width: widths?.[colIndex] }}>
              <div
                className="skeleton"
                style={{
                  height: '20px',
                  width: '80%',
                  borderRadius: '4px',
                  margin: '5px 0',
                }}
              />
            </td>
          ))}
        </tr>
      ));

  return (
    <>
      <table className="phn-data-table">
        <thead>
          <tr className="phn-data-table-header">
            <th className="phn-data-table-index"></th>
            {headers.map((header, index) => (
              <th key={index} style={{ width: widths?.[index] }}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? renderSkeletonRows()
            : rows.map((row, index) => (
                <tr
                  key={index}
                  className={`phn-data-table-row ${
                    isSelectable && isRowSelected?.(index) ? 'selected' : ''
                  } ${isRowDisabled?.(index) ? 'disabled' : ''}`}
                  onClick={() => onRowClick?.(index)}
                >
                  <td className="phn-data-table-index">
                    {startIndex + index + 1}
                  </td>
                  {row.map((data, colIndex) => (
                    <td key={colIndex} style={{ width: widths?.[colIndex] }}>
                      {data}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
      {!loading && totalPages > 1 && (
        <div className="phn-data-table-pagination">
          <button onClick={() => onPageChange(page - 1)} disabled={page === 1}>
            Previous
          </button>
          <span>{`Page ${page} of ${totalPages}`}</span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};

export default DataTable;
